import { runMediaKind, runShortsEligible, AUDIO_SHORTS_MESSAGE, UNKNOWN_SHORTS_MESSAGE } from '../lib/mediaPolicy';
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import {
  X,
  Info,
  FileText,
  Film,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Clock,
  ScrollText,
  Share2,
  Check,
} from 'lucide-react';
import {
  getRunStatus,
  getStage,
  stageBusy,
  getRunTitle,
  TONE_CLASSES,
} from '../lib/runHelpers';
import { useAuth } from '../context/AuthContext';
import { useShortsAccess } from '../hooks/useShortsAccess';
import { createShorts, createMapping, generateShareLink, getShortDownloadLink } from '../lib/valuebellApi';
import { toast } from 'sonner';

import { InfoTab } from './episode-tabs/InfoTab';
import { ShowNotesTab } from './episode-tabs/ShowNotesTab';
import { TranscriptTab } from './episode-tabs/TranscriptTab';
import { ShortsTab } from './episode-tabs/ShortsTab';

const STATUS_ICON = {
  pending: Clock,
  queued: Clock,
  transcribing: Loader2,
  mapping: Loader2,
  'creating-shorts': Loader2,
  done: CheckCircle2,
  error: AlertCircle,
};

export const PodcastDialog = ({ run, open, onOpenChange }) => {
  const [activeTab, setActiveTab] = useState('info');
  const [creatingShortsLoading, setCreatingShortsLoading] = useState(false);
  const [creatingShortsError, setCreatingShortsError] = useState(null);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [mappingError, setMappingError] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const { user } = useAuth();
  const { hasAccess: hasShortsAccess } = useShortsAccess(user);

  // Reset when opening a different run
  useEffect(() => {
    if (open) {
      setActiveTab('info');
      setCreatingShortsLoading(false);
      setCreatingShortsError(null);
      setMappingLoading(false);
      setMappingError(null);
      setShareLoading(false);
      setShareUrl(null);
    }
  }, [open, run?.id]);

  if (!run) return null;

  const status = getRunStatus(run);
  const tone = TONE_CLASSES[status.tone] || TONE_CLASSES.amber;
  const StatusIcon = STATUS_ICON[status.key] || Clock;
  const title = getRunTitle(run);

  const mediaKind = runMediaKind(run);
  const shortsEligible = runShortsEligible(run);
  const shortsStage = getStage(run, 'shorts');
  const mappingStage = getStage(run, 'mapping');
  const shortsStillProcessing = stageBusy(shortsStage) && mappingStage.status !== 'failed';
  const showCreateShortsButton = shortsEligible && !shortsStillProcessing && shortsStage.status !== 'done' && !['error', 'failed'].includes(status.key);
  const handleCreateMapping = async () => {
    setMappingLoading(true);
    setMappingError(null);
    try { await createMapping(user, run.id); }
    catch (err) { setMappingError(err.message); }
    finally { setMappingLoading(false); }
  };

  const handleCreateShorts = async () => {
    if (!shortsEligible) return;
    setCreatingShortsLoading(true);
    setCreatingShortsError(null);
    try {
      await createShorts(user, run.id);
    } catch (err) {
      setCreatingShortsError(err.message);
    } finally {
      setCreatingShortsLoading(false);
    }
  };

  const handleShare = async () => {
    if (shareUrl) {
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard');
      } catch {
        toast.error('Failed to copy link');
      }
      return;
    }

    setShareLoading(true);
    try {
      const { shareToken } = await generateShareLink(user, run.id);
      const url = `${window.location.origin}/share/${shareToken}`;
      setShareUrl(url);
      await navigator.clipboard.writeText(url);
      toast.success('Share link copied to clipboard');
    } catch (err) {
      toast.error('Failed to generate share link');
    } finally {
      setShareLoading(false);
    }
  };

  const TabButton = ({ value, icon: Icon, label }) => {
    const active = activeTab === value;
    return (
      <button
        onClick={() => setActiveTab(value)}
        className={`flex-1 sm:flex-none flex items-center justify-center sm:justify-start gap-1.5 sm:gap-3 px-2.5 sm:px-4 py-2 sm:py-3 rounded-xl transition-all duration-200 text-xs sm:text-base ${
          active
            ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg'
            : 'text-slate-400 hover:text-white hover:bg-brand-surface-2'
        }`}
      >
        <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
        <span className="font-medium">{label}</span>
      </button>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="episode-dialog max-w-6xl h-[90vh] sm:h-[85vh] p-0 bg-brand-bg border-brand-surface-3 overflow-hidden flex w-[95vw] sm:w-full">
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <div className="flex h-full w-full flex-col sm:flex-row">
          {/* Sidebar */}
          <div className="w-full sm:w-64 bg-brand-surface border-b sm:border-b-0 sm:border-r border-brand-surface-3 p-3 sm:p-6 flex sm:flex-col flex-shrink-0">
            <div className="hidden sm:block mb-8">
              <h2 className="text-xl font-bold text-white mb-2">Menu</h2>
              <div className="w-12 h-1 bg-gradient-to-r from-orange-400 to-red-500 rounded-full" />
            </div>

            <nav className="flex sm:flex-col flex-1 gap-1.5 sm:gap-2 w-full">
              <TabButton value="info" icon={Info} label="Info" />
              <TabButton value="transcript" icon={ScrollText} label="Transcript" />
              <TabButton value="notes" icon={FileText} label="Show Notes" />
              {hasShortsAccess && <TabButton value="shorts" icon={Film} label="Shorts" />}
            </nav>

            {/* Share button */}
            <button
              onClick={handleShare}
              disabled={shareLoading || status.key === 'error'}
              className="hidden sm:flex items-center justify-center gap-2 mt-4 px-4 py-2.5 bg-brand-surface-2 hover:bg-brand-surface-3 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl transition-all duration-200 text-sm font-medium border border-brand-surface-3 hover:border-orange-500/40"
            >
              {shareLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : shareUrl ? (
                <Check className="w-4 h-4 text-green-400" />
              ) : (
                <Share2 className="w-4 h-4" />
              )}
              {shareUrl ? 'Link Copied!' : 'Share'}
            </button>
          </div>

          {/* Main content */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-800">
            {/* Close */}
            <button
              onClick={() => onOpenChange(false)}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-brand-surface hover:bg-brand-surface-2 rounded-full flex items-center justify-center transition-colors duration-200"
            >
              <X className="w-5 h-5 text-white" />
            </button>

            {activeTab === 'info' && (
              <InfoTab run={run} status={status} StatusIcon={StatusIcon} tone={tone} />
            )}

            {activeTab === 'notes' && (
              <ShowNotesTab run={run} status={status} onCreate={handleCreateMapping} loading={mappingLoading} error={mappingError} />
            )}

            {activeTab === 'transcript' && (
              <TranscriptTab run={run} status={status} />
            )}

            {hasShortsAccess && activeTab === 'shorts' && (
              <div>
              {!shortsEligible && <p className="p-6 text-sm text-slate-300">{mediaKind === 'audio' ? AUDIO_SHORTS_MESSAGE : UNKNOWN_SHORTS_MESSAGE}</p>}
              <ShortsTab
                getDownloadLink={short => getShortDownloadLink(user, run.id, short.shortId)}
                run={run}
                status={status}
                showCreateButton={showCreateShortsButton}
                onCreateShorts={handleCreateShorts}
                creatingShortsLoading={creatingShortsLoading}
                creatingShortsError={creatingShortsError}
              />
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
